import React, { useState, useEffect } from 'react';
import { QrCode, Clock, ShieldCheck, Copy, CheckCircle2, User, Users, AlertTriangle } from 'lucide-react';

// ✅ IMPORTACIONES CON RUTAS ABSOLUTAS
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';

const LlaveAcceso = () => {
  const { user } = useAuth(); // Identidad real desde Firebase
  const { ninos } = useGlobal(); // Datos globales de los exploradores
  
  const [horaActual, setHoraActual] = useState('');
  const [copiado, setCopiado] = useState(false);
  
  // Verificamos si es un acceso temporal
  const esInvitado = user?.role === 'invitado' || user?.role === 'recepcion'; 
  
  const datosUsuario = {
    id: user?.uid?.slice(-6).toUpperCase() || "INV-01",
    nombre: user?.displayName || "Padre de Familia",
    codigoFamilia: user?.familyId || "FAM-PROV-2026",
  };

  // --- RELOJ DE SINCRONIZACIÓN (Protección contra capturas de pantalla) ---
  useEffect(() => {
    const actualizarHora = () => {
      setHoraActual(new Date().toLocaleTimeString('es-MX', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit', 
        hour12: true 
      }));
    };
    
    actualizarHora();
    const timer = setInterval(actualizarHora, 1000);
    return () => clearInterval(timer);
  }, []);

  const copiarCodigo = () => {
    navigator.clipboard.writeText(datosUsuario.codigoFamilia);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto py-8 md:py-12 px-4 flex flex-col lg:flex-row gap-12 items-center justify-center animate-in zoom-in-95 duration-700 font-sans text-left">
      
      {/* --- PASE DIGITAL (QR) --- */}
      <div className="relative group w-full max-w-md">
        <div className="absolute -inset-4 bg-gradient-to-tr from-amber-500 to-indigo-500 rounded-[5rem] blur-2xl opacity-10" />
        <div className="relative bg-white rounded-[4rem] shadow-2xl border-4 border-slate-100 overflow-hidden">
          
          <div className="bg-[#0A2440] p-8 text-center text-amber-500 flex flex-col items-center gap-2">
             <ShieldCheck size={32} />
             <span className="text-[10px] font-black uppercase tracking-[0.3em] italic">Carnet Digital de Seguridad</span>
             <span className="text-white font-black text-xl italic uppercase tracking-tighter">{datosUsuario.nombre}</span>
          </div>

          <div className="p-10 space-y-8 flex flex-col items-center">
            {/* CONTENEDOR DEL QR CON EFECTO DE ESCANEO */}
            <div className="w-full aspect-square bg-slate-50 rounded-[3.5rem] flex items-center justify-center border-4 border-dashed border-indigo-200 relative shadow-inner overflow-hidden">
               <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-400 shadow-[0_0_20px_#34d399] animate-scan opacity-60" />
               
               <QrCode className="w-2/3 h-2/3 text-slate-900" strokeWidth={1} />
               
               <div className="absolute bottom-6 bg-white/80 backdrop-blur-md px-4 py-1 rounded-full border border-slate-200">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">ID VERIFICADO: {datosUsuario.id}</p>
               </div>
            </div>

            {/* VALIDACIÓN TEMPORAL */}
            <div className="w-full py-6 bg-slate-50 rounded-[2.5rem] border-2 border-slate-100 flex flex-col items-center shadow-sm">
               <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 italic">Sincronización con Torre</span>
               <div className="flex items-center gap-3 text-3xl font-black font-mono text-indigo-700 italic tabular-nums">
                  <Clock size={28} className="text-indigo-500" /> {horaActual}
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- INFORMACIÓN DE VÍNCULO --- */}
      <div className="max-w-md w-full space-y-8">
        
        {!esInvitado ? (
          <div className="bg-indigo-600 p-10 rounded-[3.5rem] text-white shadow-2xl relative overflow-hidden border-4 border-indigo-500">
             <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" />
             <div className="relative z-10 space-y-6">
                <div className="flex items-center gap-3">
                  <Users size={32} className="text-indigo-200"/>
                  <h3 className="text-2xl font-black italic tracking-tighter uppercase leading-none">Vincular Cónyuge</h3>
                </div>
                
                <div className="bg-white/10 p-6 rounded-3xl border-2 border-white/20 flex items-center justify-between shadow-inner">
                   <span className="text-3xl font-black tracking-widest">{datosUsuario.codigoFamilia}</span>
                   <button onClick={copiarCodigo} className="p-4 bg-white text-indigo-600 rounded-2xl hover:bg-amber-400 hover:text-slate-900 transition-all shadow-xl active:scale-95">
                      {copiado ? <CheckCircle2 size={28} /> : <Copy size={28} />}
                   </button>
                </div>
                <p className="text-sm font-bold opacity-80 leading-relaxed italic text-left">
                  "Comparte este código con tu pareja para que ambos tengan acceso al registro y retiro de sus hijos."
                </p>
             </div>
          </div>
        ) : (
          <div className="bg-blue-50 p-10 rounded-[3.5rem] border-4 border-blue-200 shadow-xl space-y-4 text-left">
             <div className="flex items-center gap-3 text-blue-700 mb-2">
                <User size={32} />
                <h3 className="text-2xl font-black italic tracking-tighter uppercase">Acceso Temporal</h3>
             </div>
             <p className="text-base font-bold text-slate-700 leading-relaxed italic">
                Este carnet es válido únicamente para el servicio de hoy y permite el retiro seguro de los menores asignados.
             </p>
          </div>
        )}

        {/* ADVERTENCIA DE SEGURIDAD */}
        <div className="bg-amber-50 p-8 rounded-[3rem] border-2 border-amber-200 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left shadow-sm">
           <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center shrink-0">
              <AlertTriangle size={32} className="text-amber-600" />
           </div>
           <div>
             <h4 className="text-[10px] font-black text-amber-900 uppercase tracking-widest mb-1 italic">Protocolo de Privacidad</h4>
             <p className="text-[10px] font-bold text-amber-800 leading-relaxed">
               Este carnet es personal e intransferible. <span className="text-rose-600 font-black uppercase">Se prohíbe el uso de capturas de pantalla</span> para garantizar la seguridad de los menores.
             </p>
           </div>
        </div>
        
      </div>
    </div>
  );
};

export default LlaveAcceso;